'use client';

import { ReadonlyURLSearchParams } from 'next/navigation';

import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';

import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { ADOBE_GRS_TARGET_SEGMENT_LOCAL_STORAGE_KEY } from '@/constants/index';
import {
    getGRSFilterBy,
    getSearchResultsType,
} from '@/services/search/services/Grs';
import { SearchResult, WarehouseCookieValue } from '@/services/search/types';
import { SearchServiceProps } from '@/services/search/types';
import { getLocationsFromCookie } from '@/services/search/utils';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import { SearchResultGRS } from '@/src/services/search/services/GRS.types';
import type {
    ContentStackEntryDataProps,
    GRSFacetConfigProperties,
} from '@/src/types/contentStack';
import { SearchPageDigitalDataLayerProps } from '@/src/types/pages/search';
import wcsUtils from '@/src/utils/WCS';
import { getLocalWarehouseFromCookies } from '@/src/utils/cookieUtils';
import { SearchResultLW } from '@/types/searchQuery';
import {
    useBrowseContext,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import {
    populateCommonSearchDigitalData,
    populateFusionDigitalData,
} from './lib';

/**
 * Function to stringify the keyword if it is an array
 * @param keyword
 * @returns
 */
export function stringifyKeyword(keyword: string | string[] | undefined) {
    return Array.isArray(keyword) ? keyword[0] : keyword;
}
/**
 * Function to populate fusion props for analytics
 * @param searchParams
 * @param searchResult
 * @returns
 */
export function populateFusionAnalyticProps(
    searchParams?: ReadonlyURLSearchParams,
    searchResult?: SearchResult,
    searchService?: SearchServiceProps,
    sortType?: string,
    grsFacetConfig?: ContentStackEntryDataProps<GRSFacetConfigProperties>,
    whloc?: string,
    categoryTitle?: string,
    mdo?: string,
) {
    const experimentIds =
        typeof window !== 'undefined'
            ? (() => {
                  const val = localStorage.getItem(
                      ADOBE_GRS_TARGET_SEGMENT_LOCAL_STORAGE_KEY,
                  );
                  return val !== null ? [val] : ['def'];
              })()
            : ['def'];
    if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
        const searchResultGRS = searchResult as SearchResultGRS;
        const filter = getGRSFilterBy({
            searchParams,
            whloc,
            grsFacetConfig,
            categoryPageId: categoryTitle,
            mdoLocationId: mdo,
        });

        const keywordParam = stringifyKeyword(
            searchParams?.get('keyword') || '',
        );

        return {
            actualSearchTerm: keywordParam,
            attributionToken: searchResultGRS?.searchResult?.attributionToken,
            experimentIds,
            filter,
            orderBy: sortType,
            searchProvider: experimentIds?.[0]?.toUpperCase(),
            searchResultsType: getSearchResultsType(searchResultGRS),
            searchTerm:
                searchResultGRS?.searchResult?.correctedQuery || keywordParam,
        };
    } else {
        const searchResultLW = searchResult as SearchResultLW;

        return {
            actualSearchTerm: searchResultLW?.metrics?.original
                ? searchResultLW?.metrics?.original
                : stringifyKeyword(searchParams?.get('keyword') || ''),
            searchTerm: searchResultLW?.metrics?.correction
                ? searchResultLW?.metrics?.correction
                : stringifyKeyword(searchParams?.get('keyword') || ''),
            filter: [searchParams?.get('refine')],
            fusionQueryId: searchResultLW?.metrics?.queryId,
            orderBy: sortType,
            searchProvider: experimentIds?.[0]?.toUpperCase(),
            xFusionExitCode: searchResultLW?.metrics?.xFusionExitCode,
            experimentIds,
        };
    }
}

const SearchPageDigitalDataLayer: React.FC<SearchPageDigitalDataLayerProps> = ({
    lang,
    searchProps,
    pageCrumbs,
    pageName,
    productData,
    site,
}) => {
    const [cookies] = useCookies();

    useEffect(() => {
        const sessionStatus = wcsUtils.isWcsSessionEstablished()
            ? 'Authenticated'
            : 'Unauthenticated';

        const isBD = process.env.NEXT_PUBLIC_SITE?.endsWith('BD');

        if (site && lang) {
            populateCommonSearchDigitalData(
                lang,
                getLocalWarehouseFromCookies(cookies, { isBD }),
                sessionStatus,
                site,
                pageName,
                pageCrumbs,
            );
        } else if (searchProps) {
            populateFusionDigitalData(searchProps, pageName);
        } else if (productData) {
            const productExistsInList = window.digitalData?.products?.some(
                item =>
                    item.pid === productData.pid &&
                    productData.searchDisplayRank === item.searchDisplayRank,
            );

            if (!productExistsInList) {
                window?.digitalData?.products?.push(productData);
            }
        }
    }, [site, lang, searchProps, productData, cookies, pageCrumbs, pageName]);
    return null;
};

export const SearchPageDigitalDataLayerWithSearchResults = ({
    grsFacetConfig,
    categoryTitle,
    lang,
    site,
    ...rest
}: SearchPageDigitalDataLayerProps) => {
    const { queryParams: searchParams } = useQueryParams();
    const { searchResult, searchService, sortType } = useSearchContext();
    const { warehouse } = useBrowseContext();
    // TODO: Use this from context over deriving it manually like this.
    let { mdo } = getLocationsFromCookie(warehouse as WarehouseCookieValue);

    const whloc = warehouse?.nearestWarehouse?.catalog;

    const searchProps = populateFusionAnalyticProps(
        searchParams,
        searchResult,
        searchService,
        sortType,
        grsFacetConfig,
        whloc,
        categoryTitle,
        mdo,
    );

    return (
        <>
            <SearchPageDigitalDataLayer searchProps={searchProps} {...rest} />
            <SearchPageDigitalDataLayer lang={lang} site={site} {...rest} />
        </>
    );
};

export default SearchPageDigitalDataLayer;
